/**
 * Dashboard widget colour palette.
 *
 * Every value is a CSS colour string that reads a design system token
 * (conventions/design/README.md, "Data and numbers" > "Charts"). The
 * tokens have a light value and a dark value, so a chart follows the app
 * theme with no theme read in JS. Recharts and the heatmaps draw SVG, and
 * SVG paint accepts `var()` and `color-mix()`. Do not give these strings
 * to a canvas: a canvas cannot resolve `var()`.
 *
 * Wherever a widget renders more than one series (line, area, bar,
 * stacked), assign colours with `colorForSeries(index)`, so every widget
 * on every dashboard uses one series order.
 *
 * Semantic colours (good, bad, flat) colour the delta line of the KPI
 * widgets. They are neutral greys, so the colour of a delta never
 * signals "this is good" or "this is bad".
 */

/**
 * Series colours in series order. The order is the same as SERIES_TOKENS
 * in the ML Studio chart theme (ml-studio/src/Charts/chartTheme.ts), so a
 * series has the same colour on a dashboard and in ML Studio. Series 1 is
 * green: the main or highlighted series. Series 2 to 4 are ink and greys.
 * Series 5 to 8 are green and grey primitives at a clearly different
 * lightness. Label every series.
 */
export const SERIES_COLORS: readonly string[] = [
  "var(--chart-series-1)",
  "var(--chart-series-2)",
  "var(--chart-series-3)",
  "var(--chart-series-4)",
  "var(--p-green-600)",
  "var(--p-grey-500)",
  "var(--p-green-300)",
  "var(--p-green-900)",
];

export function colorForSeries(index: number): string {
  return SERIES_COLORS[((index % SERIES_COLORS.length) + SERIES_COLORS.length) % SERIES_COLORS.length];
}

/**
 * Text colours for a delta. A good change and a bad change are both
 * `--text-2`. The arrow and the number state the direction. The design
 * system has no colour for a good or a bad change: green means running
 * and selected, and there is no red. A flat change is `--text-3`.
 */
export const SEMANTIC_COLORS = {
  success: "var(--text-2)",
  warning: "var(--text-2)",
  error: "var(--text-2)",
  info: "var(--text-2)",
  muted: "var(--text-3)",
  neutral: "var(--text-2)",
} as const;

export type SemanticColor = keyof typeof SEMANTIC_COLORS;

/**
 * Sequential single-hue palette for heatmaps and density visualizations.
 *
 * `INTENSITY_PALETTE[0]` is "no data" or zero (`--chart-empty`, an empty
 * slot). `INTENSITY_PALETTE[6]` is the strongest (`--chart-series-1`, the
 * one series of the heatmap). The steps between mix the two tokens, so
 * the ramp follows the theme.
 */
export const INTENSITY_PALETTE: readonly string[] = [
  "var(--chart-empty)",
  "color-mix(in srgb, var(--chart-series-1) 16%, var(--chart-empty))",
  "color-mix(in srgb, var(--chart-series-1) 30%, var(--chart-empty))",
  "color-mix(in srgb, var(--chart-series-1) 45%, var(--chart-empty))",
  "color-mix(in srgb, var(--chart-series-1) 62%, var(--chart-empty))",
  "color-mix(in srgb, var(--chart-series-1) 80%, var(--chart-empty))",
  "var(--chart-series-1)",
];

/**
 * Pick a bucket from `INTENSITY_PALETTE` for a normalized value in [0, 1].
 * Out-of-range values clamp to the endpoints so callers don't have to
 * defend against NaN / negative inputs.
 */
export function intensityColor(normalized: number): string {
  if (!Number.isFinite(normalized) || normalized <= 0) return INTENSITY_PALETTE[0];
  if (normalized >= 1) return INTENSITY_PALETTE[INTENSITY_PALETTE.length - 1];
  const index = Math.min(
    INTENSITY_PALETTE.length - 1,
    Math.floor(normalized * INTENSITY_PALETTE.length),
  );
  return INTENSITY_PALETTE[index];
}

/**
 * Diverging palette for symmetric values around a midpoint (e.g. delta
 * vs prior period). Index 0 is most-negative, the middle is neutral,
 * the last is most-positive. Use with `divergingColor(value, midpoint)`.
 * Negative values use the ink series, positive values the green series,
 * and the midpoint is an empty slot.
 */
export const DIVERGING_PALETTE: readonly string[] = [
  "var(--chart-series-2)", // strong negative
  "color-mix(in srgb, var(--chart-series-2) 62%, var(--chart-empty))",
  "color-mix(in srgb, var(--chart-series-2) 32%, var(--chart-empty))",
  "var(--chart-empty)", // neutral
  "color-mix(in srgb, var(--chart-series-1) 32%, var(--chart-empty))",
  "color-mix(in srgb, var(--chart-series-1) 62%, var(--chart-empty))",
  "var(--chart-series-1)", // strong positive
];

export function divergingColor(value: number, midpoint = 0): string {
  if (!Number.isFinite(value)) return DIVERGING_PALETTE[3];
  const delta = value - midpoint;
  if (delta === 0) return DIVERGING_PALETTE[3];
  // Map +/- via a soft saturation curve so a tiny delta doesn't render
  // at full intensity. We treat |delta| >= 1 as "max."
  const magnitude = Math.min(1, Math.abs(delta));
  const offset = Math.round(magnitude * 3);
  return delta > 0
    ? DIVERGING_PALETTE[3 + offset]
    : DIVERGING_PALETTE[3 - offset];
}
